import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import orgService from './orgService'
import apiKeyService from './apiKeyService'

const WORKSPACE_KEY = 'activeWorkspace'
const storedWorkspace =
  (typeof localStorage !== 'undefined' && localStorage.getItem(WORKSPACE_KEY)) || 'personal'

const initialState = {
  myOrgs: [], // [{ _id, name, slug, role }]
  activeWorkspace: storedWorkspace, // 'personal' | <slug>
  activeOrg: null, // detail for the Organisation page
  invites: [],
  invitePreview: null, // for the AcceptInvite page
  apiKeys: [], // public v1 API keys for the active org [{id, name, keyPrefix, lastUsedAt, revokedAt, createdAt}]
  // The raw key is only ever returned once, by createApiKey — held here just
  // long enough for the "copy your key now" dialog, then cleared.
  lastCreatedApiKeyRaw: null,
  isLoading: false,
  isError: false,
  isSuccess: false,
  message: '',
}

const withToken = (thunkAPI) => thunkAPI.getState().auth.user.token
const errMessage = (error) =>
  (error.response && error.response.data && error.response.data.message) ||
  error.message ||
  error.toString()

const thunk = (name, fn) =>
  createAsyncThunk(`org/${name}`, async (arg, thunkAPI) => {
    try {
      return await fn(arg, thunkAPI)
    } catch (error) {
      return thunkAPI.rejectWithValue(errMessage(error))
    }
  })

export const getMyOrgs = thunk('getMyOrgs', (_, api) => orgService.listMyOrgs(withToken(api)))
export const createOrg = thunk('createOrg', (data, api) => orgService.createOrg(data, withToken(api)))
export const getOrg = thunk('getOrg', (slug, api) => orgService.getOrg(slug, withToken(api)))
export const updateOrg = thunk('updateOrg', (data, api) => orgService.updateOrg(data, withToken(api)))
export const deleteOrg = thunk('deleteOrg', (data, api) => orgService.deleteOrg(data, withToken(api)))
export const leaveOrg = thunk('leaveOrg', (slug, api) => orgService.leaveOrg(slug, withToken(api)))
export const changeMemberRole = thunk('changeMemberRole', (data, api) =>
  orgService.changeMemberRole(data, withToken(api))
)
export const removeMember = thunk('removeMember', (data, api) =>
  orgService.removeMember(data, withToken(api))
)
export const getInvites = thunk('getInvites', (slug, api) =>
  orgService.listInvites(slug, withToken(api))
)
export const createInvite = thunk('createInvite', (data, api) =>
  orgService.createInvite(data, withToken(api))
)
export const resendInvite = thunk('resendInvite', (data, api) =>
  orgService.resendInvite(data, withToken(api))
)
export const revokeInvite = thunk('revokeInvite', (data, api) =>
  orgService.revokeInvite(data, withToken(api))
)
export const previewInvite = thunk('previewInvite', (inviteToken) =>
  orgService.previewInvite(inviteToken)
)
export const acceptInvite = thunk('acceptInvite', (inviteToken, api) =>
  orgService.acceptInvite(inviteToken, withToken(api))
)
export const getApiKeys = thunk('getApiKeys', (slug, api) =>
  apiKeyService.listApiKeys(slug, withToken(api))
)
export const createApiKey = thunk('createApiKey', (data, api) =>
  apiKeyService.createApiKey(data, withToken(api))
)
export const revokeApiKey = thunk('revokeApiKey', (data, api) =>
  apiKeyService.revokeApiKey(data, withToken(api))
)

export const orgSlice = createSlice({
  name: 'org',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false
      state.isError = false
      state.isSuccess = false
      state.message = ''
    },
    setWorkspace: (state, action) => {
      state.activeWorkspace = action.payload || 'personal'
      try {
        localStorage.setItem(WORKSPACE_KEY, state.activeWorkspace)
      } catch (_) {
        /* private mode */
      }
    },
    clearActiveOrg: (state) => {
      state.activeOrg = null
      state.invites = []
      state.apiKeys = []
    },
    clearLastCreatedApiKey: (state) => {
      state.lastCreatedApiKeyRaw = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMyOrgs.fulfilled, (state, action) => {
        state.myOrgs = action.payload
        // If the persisted workspace points at an org we're no longer in, reset.
        if (
          state.activeWorkspace !== 'personal' &&
          !action.payload.some((o) => o.slug === state.activeWorkspace)
        ) {
          state.activeWorkspace = 'personal'
          try {
            localStorage.setItem(WORKSPACE_KEY, 'personal')
          } catch (_) {
            /* ignore */
          }
        }
      })
      .addCase(createOrg.fulfilled, (state, action) => {
        state.myOrgs.push({
          _id: action.payload._id,
          name: action.payload.name,
          slug: action.payload.slug,
          role: 'admin',
        })
      })
      .addCase(getOrg.pending, (state) => {
        state.isLoading = true
        state.isError = false
        state.message = ''
      })
      .addCase(getOrg.fulfilled, (state, action) => {
        state.isLoading = false
        state.activeOrg = action.payload
      })
      .addCase(getOrg.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
        state.activeOrg = null
      })
      .addCase(getInvites.fulfilled, (state, action) => {
        state.invites = action.payload
      })
      .addCase(getApiKeys.fulfilled, (state, action) => {
        state.apiKeys = action.payload
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        // action.payload includes `key` (the raw value) — strip it before
        // storing in the list; only lastCreatedApiKeyRaw ever holds it.
        const { key, ...dto } = action.payload
        state.apiKeys.unshift(dto)
        state.lastCreatedApiKeyRaw = key
      })
      .addCase(revokeApiKey.fulfilled, (state, action) => {
        const i = state.apiKeys.findIndex((k) => k.id === action.payload.id)
        if (i !== -1) state.apiKeys[i] = action.payload
      })
      .addCase(previewInvite.pending, (state) => {
        state.isLoading = true
        state.isError = false
        state.invitePreview = null
        state.message = ''
      })
      .addCase(previewInvite.fulfilled, (state, action) => {
        state.isLoading = false
        state.invitePreview = action.payload
      })
      .addCase(previewInvite.rejected, (state, action) => {
        state.isLoading = false
        state.isError = true
        state.message = action.payload
      })
      // Generic loading/error handling for the mutating thunks
      .addMatcher(
        (action) => action.type.startsWith('org/') && action.type.endsWith('/pending'),
        (state) => {
          state.isSuccess = false
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('org/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.isError = true
          state.message = action.payload
        }
      )
      .addMatcher(
        (action) => action.type.startsWith('org/') && action.type.endsWith('/fulfilled'),
        (state) => {
          state.isSuccess = true
          state.isError = false
        }
      )
  },
})

export const { reset, setWorkspace, clearActiveOrg, clearLastCreatedApiKey } = orgSlice.actions
export default orgSlice.reducer
